import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Colour wheel filter. Each colour family is drawn as its true arc of the
// hue circle, and bulges outward by how many frames contain that colour — so
// the wheel doubles as a picture of the archive's palette. The centre picks
// the monochrome frames. Clicking the active family again clears the filter.
// ---------------------------------------------------------------------------

// Hue arcs (degrees, start → end, wrapping) — must match FAMILY_EDGES in
// camera-api-new/palette-extract.js, which fills palette.js `mix` in this order.
export const COLOUR_FAMILIES = [
  { key: 'red', name: 'Red', from: 345, to: 15 },
  { key: 'orange', name: 'Orange', from: 15, to: 38 },
  { key: 'gold', name: 'Gold', from: 38, to: 62 },
  { key: 'green', name: 'Green', from: 62, to: 150 },
  { key: 'teal', name: 'Teal', from: 150, to: 190 },
  { key: 'blue', name: 'Blue', from: 190, to: 250 },
  { key: 'purple', name: 'Purple', from: 250, to: 300 },
  { key: 'pink', name: 'Pink', from: 300, to: 345 },
];

const SIZE = 180;
const C = SIZE / 2;
const R_INNER = 30;    // monochrome button radius
const R_RING = 36;     // where the colour arcs start
const T_MIN = 7;       // arc thickness for a family with a single frame
const T_MAX = 46;      // arc thickness for the most common family
const GAP = 1.6;       // degrees between families
const SLICE = 3;       // degrees per gradient slice

const span = (f) => (f.to - f.from + 360) % 360;

// Annular sector, angles in degrees clockwise from 12 o'clock.
const sector = (a0, a1, r0, r1) => {
  const pt = (a, r) => {
    const rad = ((a - 90) * Math.PI) / 180;
    return `${(C + r * Math.cos(rad)).toFixed(2)} ${(C + r * Math.sin(rad)).toFixed(2)}`;
  };
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${pt(a0, r0)} L${pt(a0, r1)} A${r1} ${r1} 0 ${large} 1 ${pt(a1, r1)} L${pt(a1, r0)} A${r0} ${r0} 0 ${large} 0 ${pt(a0, r0)}Z`;
};

const ColourWheel = ({ counts, monoCount, value, onChange }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...counts);

  const shown = hover ?? value;
  const label = shown == null
    ? 'All colours'
    : shown === 'mono'
      ? 'Monochrome'
      : COLOUR_FAMILIES[shown].name;
  const shownCount = shown == null ? null : shown === 'mono' ? monoCount : counts[shown];

  const pick = (next) => onChange(next === value ? null : next);
  const keyPick = (next) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(next); }
  };

  return (
    <div className={`kk-wheel ${value != null ? 'has-value' : ''}`}>
      <svg
        className="kk-wheel__svg"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="group"
        aria-label="Filter by colour"
        onPointerLeave={() => setHover(null)}
      >
        {COLOUR_FAMILIES.map((f, i) => {
          const n = counts[i];
          const empty = n === 0;
          const r1 = R_RING + (empty ? 3 : T_MIN + (T_MAX - T_MIN) * Math.sqrt(n / max));
          const a0 = f.from + GAP / 2;
          const a1 = f.from + span(f) - GAP / 2;
          const slices = [];
          for (let a = a0; a < a1; a += SLICE) {
            const b = Math.min(a1, a + SLICE + 0.4); // slight overlap hides seams
            slices.push(
              <path key={a} d={sector(a, b, R_RING, r1)} fill={`hsl(${Math.round(a + SLICE / 2) % 360} 64% 52%)`} />
            );
          }
          return (
            <g
              key={f.key}
              className={`kk-wheel__family ${value === i ? 'is-on' : ''} ${empty ? 'is-empty' : ''}`}
              role={empty ? undefined : 'button'}
              tabIndex={empty ? undefined : 0}
              aria-pressed={empty ? undefined : value === i}
              aria-label={empty ? undefined : `${f.name}, ${n} frames`}
              onClick={empty ? undefined : () => pick(i)}
              onKeyDown={empty ? undefined : keyPick(i)}
              onPointerEnter={empty ? undefined : () => setHover(i)}
              onFocus={empty ? undefined : () => setHover(i)}
              onBlur={() => setHover(null)}
            >
              {slices}
              <path className="kk-wheel__outline" d={sector(a0, a1, R_RING, r1)} />
              {/* generous invisible hit area out to the rim */}
              <path className="kk-wheel__hit" d={sector(a0, a1, R_RING - 2, Math.max(r1, R_RING + T_MAX * 0.6))} />
            </g>
          );
        })}
        <g
          className={`kk-wheel__mono ${value === 'mono' ? 'is-on' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={value === 'mono'}
          aria-label={`Monochrome, ${monoCount} frames`}
          onClick={() => pick('mono')}
          onKeyDown={keyPick('mono')}
          onPointerEnter={() => setHover('mono')}
          onFocus={() => setHover('mono')}
          onBlur={() => setHover(null)}
        >
          <path d={`M${C} ${C - R_INNER} A${R_INNER} ${R_INNER} 0 0 0 ${C} ${C + R_INNER}Z`} fill="#e9e6e1" />
          <path d={`M${C} ${C - R_INNER} A${R_INNER} ${R_INNER} 0 0 1 ${C} ${C + R_INNER}Z`} fill="#15130f" />
          <circle className="kk-wheel__outline" cx={C} cy={C} r={R_INNER} />
        </g>
      </svg>

      <div className="kk-wheel__info" aria-live="polite">
        <span className="kk-wheel__kicker">Filter by colour</span>
        <span className={`kk-wheel__name ${hover != null && hover !== value ? 'is-preview' : ''}`}>{label}</span>
        <span className="kk-wheel__count">
          {shownCount == null ? 'Tap a colour' : `${shownCount} ${shownCount === 1 ? 'frame' : 'frames'}`}
        </span>
        {value != null && (
          <button className="kk-wheel__clear" onClick={() => onChange(null)}>
            Clear ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default ColourWheel;
